<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1 v-if="isNew">
        {{ $t('permits.add_permit') }}
      </h1>
      <h1 v-else>
        {{ $t('permits.edit_permit') }}
      </h1>
      <a v-if="!isNew" class="link link--orange" @click="showDeletingConfirmation">
        {{ $t('common.delete') }}&nbsp;
        <i class="icon icon--cross" />
      </a>
    </div>

    <div class="details-component__content-container">
      <div class="details-component__content content" :class="{ sticky: $route.name === isUnderUser }">
        <form class="form fixed-form">
          <table class="details-table no-col-padding edit-table fixed-table label-col-wide-150">
            <tr v-if="canUseCards()" class="details-table__row"
              :class="{ 'details-table__row_error': errors.has('parkingCardNr') }">
              <td class="details-table__col details-table__col_label">
                <label for="parkingCardNr" class="form__label">{{ $t('common.card_number') }}</label>
              </td>
              <td class="details-table__col">
                <el-input id="parkingCardNr" ref="parkingCardNr" v-model="form.parkingCardNr"
                  v-validate="'validParkingCardNr'" name="parkingCardNr" class="parkingCardNrInput-toggle"
                  :data-vv-as="$t('add_permit.parking_card_id')" data-vv-delay="1000" />
                <tooltip v-show="errors.has('parkingCardNr')" class="form__validation">
                  {{ $t('errors.valid_card_field') }}
                </tooltip>
              </td>
            </tr>
            <!-- Parking spot number -->
            <tr v-if="canShowPermitSpot()" class="details-table__row"
              :class="{ 'details-table__row_error': errors.has('parkingSpotNumber') }">
              <td class="details-table__col details-table__col_label">
                <label for="parkingSpotNumber" class="form__label">{{ $t('permits.parking_spot_number') }}</label>
              </td>
              <td class="details-table__col">
                <input id="parkingSpotNumber" ref="parkingSpotNumber" v-model="form.parkingSpotNumber"
                  v-validate="'max:255'" type="text" class="form__input" name="parkingSpotNumber"
                  :data-vv-as="$t('permits.parking_spot_number')" :disabled="!canSetPermitSpot()">
                <tooltip v-show="errors.has('parkingSpotNumber')" class="form__validation">
                  {{ $tc('errors.field_max', 1, {
                    max: 255, have: form.parkingSpotNumber ? form.parkingSpotNumber.length
                      : ''
                  }) }}
                </tooltip>
              </td>
            </tr>

            <tr v-if="(canSetPermitStartDate() || form.validFromDate)" class="details-table__row">
              <td class="details-table__col details-table__col_label">
                {{ $t('permits.valid_from_date') }}
              </td>
              <td class="details-table__col">
                <el-date-picker v-model="form.validFromDate" format="dd.MM.yyyy" type="date" name="validFromDate"
                  :placeholder="defaultDate" :default-value="defaultDateValue" :picker-options="startTimeOptions"
                  :readonly="!canSetPermitStartDate()" :clearable="true" value-format="dd.MM.yyyy" />
              </td>
            </tr>

            <tr v-if="(canSetPermitExpirationDate() || form.expirationDate)" class="details-table__row">
              <td class="details-table__col details-table__col_label">
                {{ $t('permits.expiration_date') }}
              </td>
              <td class="details-table__col">
                <el-date-picker v-model="form.expirationDate" format="dd.MM.yyyy" type="date" name="expirationDate"
                  :picker-options="expireTimeOptions"
                  :readonly="!canSetPermitExpirationDate() || !canUseDateExpiration()" :clearable="true"
                  value-format="dd.MM.yyyy" />
              </td>
            </tr>

            <!-- Vehicles -->
            <tr class="details-table__row details-table__row-—flex">
              <td class="details-table__col details-table__col_label">
                <label for="vehicleReg" class="form__label">{{ $tc('common.user_vehicle_reg', 1) }}</label>
              </td>
              <td class="details-table__col details-table__col--with-a-choise">
                <div class="el-input__inner el-input__inner--vehicle-reg">
                  {{ !selectedVehicle.vehicle_reg ? selectedVehicle.vehicleReg : selectedVehicle.vehicle_reg }}
                </div>
                <el-button slot="append" class="btn btn--change" @click="showListModalVehicle()">
                  {{ selectedVehicle.vehicle_reg ? $t('common.change') + ' ' + $tc('common.user_vehicle_reg', 1) :
                    $t('common.add') + ' ' + $tc('common.user_vehicle_reg', 1) }}
                </el-button>
              </td>
            </tr>

            <!-- Products -->
            <tr v-if="isUnderUser" class="details-table__row details-table__row-—flex">
              <td class="details-table__col details-table__col_label">
                <label for="product" class="form__label">{{ $tc('add_permit.product', 1) }}</label>
              </td>
              <td class="details-table__col details-table__col--with-a-choise">
                <div class="el-input__inner el-input__inner--vehicle-reg">
                  {{ selectedProductName }}
                </div>
                <el-button v-if="products.data" slot="append" class="btn btn--change" @click="showListModalProduct()">
                  {{ selectedProductName ? $t('common.change') + ' ' + $tc('add_permit.product', 1) : $t('common.add') +
                    ' ' + $tc('add_permit.product', 1) }}
                </el-button>
              </td>
            </tr>

            <tr v-if="!isUnderUser" class="details-table__row">
              <td class="details-table__col details-table__col_label">
                {{ $t('common.zone') }}
              </td>
              <td v-if="permit.product" class="details-table__col">
                <span v-for="zone in permit.product.zones" :key="zone.id">{{ zone.code }}&nbsp;</span>
              </td>
            </tr>

            <tr v-if="permit.partner && !isUnderUser" class="details-table__row">
              <td class="details-table__col details-table__col_label">
                {{ $t('common.issued_by') }}
              </td>
              <td class="details-table__col">
                {{ permit.partner.name }}
              </td>
            </tr>

            <tr v-if="permit.partner && !isUnderUser" class="details-table__row">
              <td class="details-table__col details-table__col_label">
                {{ $t('end_user.contact') }}
              </td>
              <td class="details-table__col">
                {{[permit.partner.contactPerson,
                permit.partner.phone
                  ? parsePhoneNumber(permit.partner.phone, permit.partner.phoneCountry)
                  : null,
                permit.partner.address]
                  .filter(x => x !== null)
                  .join(',&nbsp;')}}
              </td>
            </tr>
          </table>

          <div class="form__buttons buttonset">
            <button v-if="isShowPrevPermitBtn()" type="submit" class="buttonset__button btn btn--secondary"
              @click.prevent.stop="onPrevPermit()">
              {{ $t('permits.prev') }}
            </button>
            <button v-if="isShowNextPermitBtn()" type="submit" class="buttonset__button btn btn--secondary"
              @click.prevent.stop="onNextPermit()">
              {{ $t('permits.next') }}
            </button>
            <router-link :to="{ name: 'user', params: { id: $route.params.id } }"
              class="buttonset__button btn btn--secondary">
              {{ $t('common.discard') }}
            </router-link>
            <button type="submit" class="buttonset__button btn btn--main" @click.prevent.stop="onSubmit()">
              {{ $t('permits.save') }}
            </button>
          </div>
        </form>

        <v-map v-if="coordinates && coordinates.length > 0 && !isUnderUser" ref="map" :zoom="zoom" :center="center"
          class="content__cell product-map">
          <v-geojson-layer :geojson="coordinates" :options="mapOptions" />
          <v-tilelayer :url="url" />
        </v-map>
      </div>
      <div :key="permit.id" class="permit-block">
        <div class="permit-block__zone">
          <div>
            <span class="permit-icon icon-product" />
            <span v-if="selectedProductName">{{ selectedProductName }}</span>
            <span v-else>-------</span>
          </div>
          <div>
            <span class="permit-icon fa fa-map-marker" />
            <span v-if="showZone">{{ permit.zone }}</span>
            <span v-else>-------</span>
          </div>
        </div>
        <div class="permit-block__identifiers">
          <div v-if="selectedVehicle.vehicleReg" class="permit-block__identifiers__identity permit-plate">
            <div class="icon icon--car" />
            <p :class="{ 'smaller-font': selectedVehicle.vehicleReg && selectedVehicle.vehicleReg.length > 6 }">
              {{ selectedVehicle.vehicleReg }}
            </p>
            <p v-if="permit.parkingSpotNumber" class="permit-plate__spot">
              {{ permit.parkingSpotNumber }}
            </p>
          </div>
          <div v-if="permit.parkingCardNr && permit.canUseCards"
            class="permit-block__identifiers__identity permit-card permit-card__small">
            <div class="permit-card__number">
              {{ permit.parkingCardNr }}
            </div>
          </div>
          <div v-if="!selectedVehicle.vehicleReg && !permit.parkingCardNr"
            class="permit-block__identifiers__identity no-identity">
            <span>
              <i class="fa fa-exclamation-triangle" aria-hidden="true" />
              {{ $t('end_user.permit_needs_identity') }}
            </span>
          </div>
        </div>
        <div v-if="!isPermitExpired(permit) && permitValidityPeriod(permit).length > 0" class="permit-card-validity">
          {{ permitValidityPeriod(permit) }}
        </div>
        <div v-if="isPermitExpired(permit)" class="permit-card-expired">
          {{ $t('end_user.permit_expired', { expirationDate: permit.expirationDate }) }}
        </div>
      </div>
    </div>

    <ListModal v-if="showListModal" @cancel="closeListModal" @confirmed="confirmedListModal">
      <template v-if="listModalType === 'product'" #default>
        <tr v-if="isUnderUser" class="details-table__row form__field_radiobuttons auto-height"
          :class="{ 'details-table__row_error': errors.has('product') }">
          <td class="details-table__col">
            <div class="radiobuttons">
              <template v-for="product in products.data">
                <!--v-if="canAddPermitTo(product) || (permit.product && product.id === permit.product.id)"-->
                <div :key="product.id" class="radiobuttons__element">
                  <label
                    :class="{ 'disabled': !(canAddPermitTo(product) || (permit.product && product.id === permit.product.id)) }"
                    @click="onChangePack(product)">
                    <input :id="product.id" v-model="selectedProduct" v-validate="'required'" type="radio"
                      name="product" :value="product.id"
                      :disabled="!(canAddPermitTo(product) || (permit.product && product.id === permit.product.id))"
                      role="select product">
                    <span class="radiobuttons__control" />
                    <span :for="product.id" class="radiobuttons__description">{{ product.name }} - ({{
                      product.permits_in_use }}/{{ product.permits_count }}) <template
                        v-if="product.date_expiration">({{ $t('permits.expiration_date') }} {{
                          getFormattedDateExpiration(product.date_expiration) }})</template></span>
                  </label>
                </div>
              </template>
            </div>
            <tooltip v-show="errors.has('product')" class="form__validation">
              {{ $t('errors.product_field_required') }}
            </tooltip>
          </td>
        </tr>
      </template>
      <template v-else-if="listModalType === 'vehicle'" #default>

        <tr class="details-table__row details-table__row--vehicle">
          <td class="details-table__col details-table__col--vehicle">
            <h2 class="change-plate-title">{{this.$route.name === 'add-user-with-permit' ? $t('add_permit.add_plate') : $t('add_permit.change_plate')}}</h2>
            <div class="plateSearchWrapper">
              <el-input id="plateSearch" ref="plateSearchInput" v-model="plateSearch"
                        :placeholder="$t('add_permit.search_plate')"
                        v-validate="'max:15'" name="plateSearch" class="plateSearchInput"
                        @keydown.native="stopInputAtDashAndSpace"
                        @keydown.enter.native="searchPlateNumber">


              </el-input>
              <el-button @click="searchPlateNumber" class="plateSearchButton">
                <i class="el-icon-search"/>
              </el-button>
              <el-button v-if="plateSearch.length > 0" @click="clearPlateSearch" style="margin-left: 16px;">
                <i class="el-icon-close"/>
              </el-button>
            </div>
            <div v-for="vehicle in vehicles.data" :id="'plate-' + vehicle.id"
                 :class="vehicle.id === activePlate ? 'permit-edit-block active-border' : 'permit-edit-block'">
              <!-- edit permit row block -->
              <div :class="vehicle.id === selectedVehicle.id ?
                (vehicle.id === activePlate ? 'row-title' : 'row-title active-plate-bg')
                : 'row-title'">
                <!-- edit icon -->
                <div v-if="vehicle.id !== activePlate" class="edit-icon" @click="selectActivePlate(vehicle.id)" />
                <!-- plate number + parking icon -->
                <el-tag :key="vehicle.id" closable :disable-transitions="false"
                  :class="vehicle.new === true ? vehicle.id === selectedVehicle.id ? 'el-tag--success' : '' : vehicle.id === selectedVehicle.id ? (vehicle.permitId && vehicle.permitId.length > 0) ? 'el-tag--success el-tag--permit' : 'el-tag--success' : (vehicle.permitId && vehicle.permitId.length > 0) ? 'el-tag--permit' : ''"
                  role="select vehicle" @click.native="selectVehicle(vehicle)"
                  @close.stop.prevent="removeVehicle(vehicle)">
                  <div style="flex-direction: column; ">
                    <span class="vehicle-reg">{{ vehicle.vehicle_reg }}</span>
                    <span v-if="vehicle.comment && vehicle.id !== activePlate" class="vehicle-comment">
                      {{ vehicle.comment }}
                    </span>
                    <span v-else-if="vehicle.id !== activePlate" class="vehicle-comment no-comment">
                      &nbsp;
                    </span>
                  </div>
                </el-tag>
              </div>
              <!-- comment edit form -->
              <div :id="'comment-block-' + vehicle.id" :ref="'comment-block-' + vehicle.id"
                :class="vehicle.id === activePlate ? 'active-edit' : 'hidden-edit'">
                <input :ref="`comment-${vehicle.id}`" class="vehicleInput-comment" :placeholder="$t('parking.comment')"
                  :value="vehicle.comment">
                <!-- save/cancel buttons -->
                <div class="form__buttons buttonset">
                  <div class="buttonset__button btn btn--secondary btn--small" @click="selectActivePlate(0)">
                    {{ $t('common.cancel') }}
                  </div>
                  <button :ref="`confirm-btn-${vehicle.id}`" type="submit"
                    class="buttonset__button btn btn--main btn--small" @click.prevent.stop="saveComment(vehicle.id)">
                    {{ $t('common.confirm') }}
                  </button>
                </div>
              </div>
            </div>
            <div class="vehicle-count">
              <div class="vehicle-count--count">{{ $t('add_permit.shown_x_of_y', {x: vehicles.data ? vehicles.data.length : 0, y: vehicles.total}) }}</div>
              <button type="submit" class="buttonset__button btn btn--secondary"
                      v-if="vehicles.data && vehicles.data.length !== vehicles.total"
                      @click.prevent.stop="fetchMorePlates()">
                {{ $t('add_permit.show_next_x', {x: 10 }) }}
              </button>
            </div>
            <!-- new plate number form -->
            <el-input v-if="!vehicleInputDisabled" id="vehicleReg" ref="vehicleInput" v-model="form.vehicleReg"
              v-validate="'max:15|unique_car_reg'" name="vehicleReg" class="vehicleInput-toggle"
              :class="vehicleRegError ? 'vehicleInput-toggle--error' : ''" @keyup.enter.native="addVehicleReg()"
              @keydown.native="stopInputAtDashAndSpace" @focus="vehicleRegError = false"
              @input="vehicleRegError = false">


              <el-button v-if="!vehicleInputDisabled" slot="append" @click="addVehicleReg()">
                {{ $t('common.ok') }}
              </el-button>
            </el-input>
            <el-input v-if="!vehicleInputDisabled" ref="vehicleComment" v-model="form.vehicleComment"
              class="vehicleInput-comment resident" name="vehicleComment" :placeholder="$t('parking.comment')" />
            <el-button v-if="vehicleInputDisabled" class="vehicle-input" @click="vehicleInputDisabled = false">
              {{ '+ ' + $t('add_permit.add_car') }}
              <i class="el-icon-edit el-icon-right" />
            </el-button>
            <tooltip v-show="errors.has('vehicleReg:max') | errors.has('vehicleReg:unique_car_reg')"
              class="form__validation to-bottom">
              {{ $tc(errors.has('vehicleReg:unique_car_reg') ? 'errors.duplicated_vehicle_reg' : 'errors.field_max', 1,
                {
                  max: 15, have: form.vehicleReg ?
                    form.vehicleReg.length : ''
                }) }}
            </tooltip>
          </td>
        </tr>
      </template>
    </ListModal>

    <ConfirmationModal v-if="showConfirmationModal" @cancel="cancelDelete" @confirmed="deletePermit">
      <h1 slot="header">
        {{ $t('common.deleting') }}
      </h1>
      <p slot="body">
        {{ $t('permits.confirm_delete') }}
      </p>
    </ConfirmationModal>

    <ConfirmationModal v-if="showConfirmationUncheckModal" @cancel="closeConfirmationUncheckModal"
      @confirmed="uncheckVehicleActive">
      <h1 slot="header">
        {{ $t('common.vehicle_reg') }}
      </h1>
      <p slot="body">
        {{ $t('vehicle.confirm_uncheck_active') }}
      </p>
    </ConfirmationModal>

    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">
        {{ userWarningHeader }}
      </h1>
      <p slot="body">
        {{ userWarningBody }}
      </p>
    </WarningModal>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import L from 'leaflet';
import Vue2Leaflet from 'vue2-leaflet';
import { format } from 'libphonenumber-js';
import Tooltip from 'common/Tooltip';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';
import ListModal from 'common/ListModal';
import stickybits from 'stickybits';
import dechex from 'locutus/php/math/dechex';
import hexdec from 'locutus/php/math/hexdec';
import strpad from 'locutus/php/strings/str_pad';
import { sanitizeVehicleRegNumber } from '../../utils';
import { debounce } from 'lodash';

let resolveQueue = [];

const debouncedCheck = debounce((store, payload, context) => {
  store.dispatch('users/vehicles/isDuplicate', payload)
    .then(isDuplicate => {
      resolveQueue.forEach(resolve => resolve({ valid: !isDuplicate }));
      resolveQueue = [];
    })
    .catch(() => {
      resolveQueue.forEach(resolve => resolve({ valid: true }));
      resolveQueue = [];
    });
}, 500);

export default {
  components: {
    'v-map': Vue2Leaflet.Map,
    'v-tilelayer': Vue2Leaflet.TileLayer,
    'v-geojson-layer': Vue2Leaflet.GeoJSON,
    Tooltip,
    ConfirmationModal,
    WarningModal,
    ListModal,
  },
  props: {
    selectedTenant: Number,
  },
  computed: {
    isNew() {
      return !this.$route.params.permitId;
    },
    ...mapGetters({
      permitError: 'permits/permit/error',
      permit: 'permits/permit/permit',
      coordinates: 'permits/permit/coordinates',
      user: 'user/user/user',
      users: 'users/user/user',
      vehicles: 'users/vehicles/vehicles',
      vehicleError: 'users/vehicles/error',
      products: 'landlord/products/products',
      usersKeywordFilter: 'landlord/users/keywordFilter',
      permits: 'landlord/permits/permits',
    }),
    selectedUser() {
      if (this.isUnderUser) {
        return this.$route.params.id;
      }
      return this.user.data && this.user.data.selectedRole ? this.user.data.selectedRole.id : null;
    },
    isUnderUser() {
      return this.$route.name === 'user-with-permit' || this.$route.name === 'add-user-with-permit';
    },
    defaultDate() {
      return moment().format('DD.MM.YYYY');
    },
    defaultDateValue() {
      return moment().toDate();
    },
  },
  watch: {
    $route: 'fetchAllData',
    selectedTenant: 'fetchTenantData',
    // selectedUser: 'fetchVehiclePlateNumbers',
    // vehicles: 'preselectVehicle',
    selectedProduct(value) {
      this.setSelectedProductName(value);
    },
    vehicleComment(val) {
      this.value = val;
    },
  },
  created() {
    if (!this.user.authenticated) {
      return;
    }
    this.fetchAllData();
    this.resetValidators();
    this.createValidators();

    document.removeEventListener('click', window.eventListenerForEditViewPermit);
    window.eventListenerForEditViewPermit = this.handleInput;
    document.addEventListener('click', this.handleInput, false);
  },
  mounted() {
    if (!this.isUnderUser) {
      this.updateMap();
    }
    stickybits('.sticky');
    this.$validator.extend('unique_car_reg', {
      getMessage: () => 'Car registration is not unique',
      validate: value => {
        return new Promise(resolve => {
          const upperValue = value.toUpperCase();

          resolveQueue.push(resolve);

          debouncedCheck(this.$store, {
            id: this.selectedUser,
            user: this.user,
            vehicleReg: upperValue
          }, this);
        });
      }
    });

    this.$validator.attach({
      name: 'vehicleReg',
      rules: 'unique_car_reg',
    });
  },
  updated() {
    stickybits('.sticky');
  },
  destroyed() {
    this.$store.commit(`landlord/products/${mTypes.CLEAR_STATE}`);
  },
  data() {
    return {
      activePlate: null,
      selectedVehicle: {
        id: null,
      },
      primordialSelectedVehicle: this.selectedVehicle,
      isPermitSelected: false,
      form: {
        vehicleReg: '',
        vehicleComment: '',
      },
      plateSearch: '',
      vehicleLimit: 10,
      vehicleOffset: 0,
      triggerSubmit: false,
      parkingCardNrInputDisabled: true,
      vehicleInputDisabled: true,
      tenantHasProducts: false,
      tenantPermitCount: 0,
      selectedProduct: null,
      primordialSelectedProduct: this.selectProduct,
      selectedProductName: null,
      showConfirmationModal: false,
      showConfirmationUncheckModal: false,
      showWarningModal: false,
      userWarningBody: null,
      userWarningHeader: null,
      showListModal: false,
      listModalType: null,
      zoom: 15,
      center: [59.412, 24.707],
      url: 'https://a.tile.openstreetmap.org//{z}/{x}/{y}.png',
      startTimeOptions: {
        disabledDate: date => (moment(date).isAfter(moment(this.form.expirationDate, 'DD.MM.YYYY').add(-1, 'day'), 'day')),
        firstDayOfWeek: 1,
      },
      expireTimeOptions: {
        disabledDate: date => (moment(date).isBefore(moment(this.form.validFromDate, 'DD.MM.YYYY').add(1, 'day'), 'day')),
        firstDayOfWeek: 1,
      },
      mapOptions: {
        style: (feature) => {
          let color = '#E94F0A';
          if (feature.properties.color) {
            color = { color: feature.properties.color };
          } else {
            const index = Math.floor(Math.random() * 5);
            const coordinateColors = ['#E94F0A', '#389DE9', '#fb4f4f', '#00d1b2', '#fbc93d'];
            color = { color: coordinateColors[index] };
          }
          return color;
        },
        onEachFeature: (feature, layer) => {
          if (feature.properties && feature.properties.name) {
            layer.bindPopup(feature.properties.name);
          }
        },
      },
      showZone: false,
      vehicleRegError: false,
    };
  },
  methods: {
    saveComment(vehicleId) {
      try {
        this.$refs[`confirm-btn-${vehicleId}`][0].disabled = true;
        this.$store.dispatch('permits/permit/updateVehicle', {
          vehicleId,
          vehicleComment: this.$refs[`comment-${vehicleId}`][0].value,
          userId: this.selectedUser,
        }).then(() => {
          if (this.selectedUser) {
            this.$store.dispatch('users/vehicles/fetch', {
              id: this.selectedUser, user: this.user,
            });
          }
          this.fetchVehiclePlateNumbers();
          this.$refs[`confirm-btn-${vehicleId}`][0].disabled = false;
          this.activePlate = '';
        });
      } catch (e) {
        console.log('Vehicle comment update error', e);
      }
    },
    selectActivePlate(plateId) {
      this.activePlate = plateId;
    },
    selectVehicle(vehicle) {
      this.selectedVehicle = vehicle;
    },
    getCurrentPermitIndex() {
      let currentPermitIndex = -1;

      if (this.permits.items && this.permits.items.length) {
        for (let i = 0; i < this.permits.items.length; i += 1) {
          const element = this.permits.items[i];

          if (element.id === this.permit.id) {
            currentPermitIndex = i;
          }
        }
      }

      return currentPermitIndex;
    },
    isShowPrevPermitBtn() {
      const currentPermitIndex = this.getCurrentPermitIndex();

      if (currentPermitIndex > 0) {
        return true;
      }

      return false;
    },
    isShowNextPermitBtn() {
      const currentPermitIndex = this.getCurrentPermitIndex();

      if (currentPermitIndex >= 0 && currentPermitIndex !== this.permits.items.length - 1) {
        return true;
      }

      return false;
    },
    onPrevPermit() {
      const currentPermitIndex = this.getCurrentPermitIndex();

      const prevPermitId = this.permits.items[currentPermitIndex - 1].id;

      this.onSubmit({ name: 'user-with-permit', params: { id: this.$route.params.id, permitId: prevPermitId } });
    },
    onNextPermit() {
      const currentPermitIndex = this.getCurrentPermitIndex();

      const prevPermitId = this.permits.items[currentPermitIndex + 1].id;

      this.onSubmit({ name: 'user-with-permit', params: { id: this.$route.params.id, permitId: prevPermitId } });
    },
    onChangePack(product) {
      if (product.date_expiration) {
        this.form.expirationDate = product.date_expiration;
      }
      if (product.parking_spot_number) {
        this.form.parkingSpotNumber = product.parking_spot_number;
      }
    },
    hasProducts() {
      if (this.selectedTenant || this.isTenant()) {
        let productsNotFull = false;
        this.tenantPermitCount = 0;
        if (this.products.data.length > 0) {
          this.products.data.forEach((el) => {
            if (el.permits_issued < el.permit_count) {
              productsNotFull = true;
            }
            this.tenantPermitCount += el.total;
          });
        }
        this.tenantHasProducts = this.products.data.length > 0 && productsNotFull;

        if (this.isNew && !this.tenantHasProducts && this.tenantPermitCount > 0) {
          this.showWarningModal = true;
          this.userWarningHeader = this.$t('common.warning');
          this.userWarningBody = this.$t('errors.all_permits_issued', { permitCount: this.tenantPermitCount });
        }

        this.permitZoneAdd();
      }
      this.setSelectedProductName(this.selectedProduct);
    },
    fetchTenantData() {
      this.fetchProducts();
    },
    fetchProducts() {
      this.productId = null;
      if (this.isLandlord() && this.selectedTenant !== null && this.user.data && typeof this.selectedTenant !== 'undefined') {
        this.$store.dispatch('landlord/products/fetchPermitProducts', { user: this.user, id: this.selectedTenant }).then(() => {
          this.hasProducts();
        });
      } else if (this.isTenant() && this.user.data) {
        this.$store.dispatch('landlord/products/fetchPermitProducts', { user: this.user, id: this.user.data.selectedRole.id }).then(() => {
          this.hasProducts();
        });
      } else {
        this.products.data = [];
      }
    },
    canAddPermitTo(product) {
      return product.permits_in_use < product.permits_count ||
        (this.permit.product && product.id === this.permit.product.id);
    },
    fetchUsers() {
      this.$store.dispatch('landlord/users/fetch', { user: this.user, limit: this.limit, orderBy: 'updated_at', keyword: this.usersKeywordFilter });
    },
    fetchAllData() {
      if (this.$route.name === 'add-user-with-permit') {
        this.$store.commit(`permits/permit/${mTypes.CLEAR_STATE}`);
        this.fetchUsers();
        this.fetchProducts();
        this.fetchVehiclePlateNumbers();
        this.selectedProduct = null;
        return;
      }
      this.fetchPermit();
      this.fetchUsers();
      this.fetchProducts();
      this.fetchVehiclePlateNumbers();
      /* if (this.isUnderUser) {
        this.fetchProducts();
      } */
    },
    closeConfirmationUncheckModal() {
      this.showConfirmationUncheckModal = false;
    },
    uncheckVehicleActive() {
      this.selectedVehicle = { id: null };
      this.closeConfirmationUncheckModal();
    },
    callSubmitIfInputIsEnabled(buttonRef) {
      if (!this[`${buttonRef}Disabled`]) {
        this.onSubmit();
      }
    },
    addVehicleReg() {
      if (/^[a-zA-Z0-9]+$/.test(this.form.vehicleReg) === true) {
        const duplicatedVehicleReg = this.vehicles.data.find(car => car.vehicle_reg === this.form.vehicleReg.toUpperCase());
        this.vehicleInputDisabled = true;

        if (!this.form.vehicleReg || this.form.vehicleReg.length > 15 || this.form.vehicleComment > 150 || duplicatedVehicleReg) {
          if (duplicatedVehicleReg) {
            this.form.vehicleReg = '';
          }
          return;
        }

        const id = Date.now();
        const vehicleData = {
          id,
          vehicle_reg: sanitizeVehicleRegNumber(this.form.vehicleReg),
          comment: this.form.vehicleComment,
          new: true,
        };
        this.vehicles.data.push(vehicleData);
        this.vehicles.total += 1;
        this.selectedVehicle = vehicleData;
        this.form.vehicle_reg = '';
        this.form.comment = '';
      } else {
        this.vehicleRegError = true;
        this.showWarningModal = true;
        this.userWarningHeader = this.$t('common.warning');
        this.userWarningBody = this.$t('errors.not_valid_vehicle_reg');
      }
    },
    stopInputAtDashAndSpace(event) {
      if (event.key === '-' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    permitZoneAdd() {
      if (this.products.data) {
        this.permit.zone = '';
        this.products.data.forEach((product) => {
          const productAddress = product.address
            .replace(/\s+|\(.*?\)/g, '')
            .replace(/,/g, ', ');
          if (product.id === this.selectedProduct) {
            if (productAddress) {
              this.permit.zone = productAddress;
              this.showZone = true;
            } else {
              this.showZone = false;
            }
          }
        });
      }
    },
    fetchPermit() {
      if (this.$route.params.permitId && this.user.data) {
        const payload = {
          user: this.user,
          id: this.$route.params.permitId,
        };
        this.$store.dispatch('permits/permit/fetch', payload)
          .then(() => {
            if (!this.isUnderUser) {
              this.updateMap();
            }
            this.form = this.permit;
            this.setPermitRelatedVehicle();
            if (this.permit.product && this.permit.product.id && this.isUnderUser) {
              /* this.selectedTenant = this.permit.partner.id; */
              this.selectedProduct = this.permit.product.id;
              this.primordialSelectedProduct = this.selectedProduct;
            }
          });
      } else {
        this.$store.commit(`permits/permit/${mTypes.CLEAR_STATE}`);
      }
    },
    fetchVehiclePlateNumbers() {
      if (this.selectedUser) {
        const payload = {
          user: this.user,
          id: this.selectedUser,
          offset: this.vehicleOffset,
          limit: this.vehicleLimit,
        };
        this.$store.dispatch('users/vehicles/fetch', payload)
          .then(() => {
            this.vehicles.data.sort((a, b) => this.dynamicSort('id', a, b));
            this.checkingNumberForPermit();
            this.setPermitRelatedVehicle();
          });
      }
    },
    setPermitRelatedVehicle() {
      if (this.permit.vehicle) {
        this.selectedVehicle = this.permit.vehicle;
        this.primordialSelectedVehicle = this.selectedVehicle;
      } else {
        this.selectedVehicle = { id: null };
      }
    },
    checkingNumberForPermit() {
      this.vehicles.data.forEach((vehicle) => {
        this.users.vehicles.forEach((item) => {
          if (vehicle.id === item.id) {
            vehicle.permitId = item.permitId;
          }
        });
      });
    },
    updateMap() {
      if (this.coordinates) {
        const bounds = L.geoJSON(this.coordinates).getBounds();
        if (bounds.isValid() && this.$refs.map) {
          this.$refs.map.fitBounds(bounds);
        }
      }
    },
    parsePhoneNumber(phone, code) {
      return phone
        ? format({ country: code, phone: phone.toString() }, 'International_plaintext')
        : '';
    },
    onSubmit(routerParams = { name: 'user', params: { id: this.$route.params.id } }) {
      this.$validator.validateAll()
        .then((success) => {
          if (success) {
            this.$store.commit(`permits/permit/${mTypes.DATA_ERROR}`, null);

            // If user forgot to click OK
            if (!this.vehicleInputDisabled) {
              this.addVehicleReg();
            }
            if (this.selectedVehicle.new) {
              this.form.vehicle = { vehicleReg: this.selectedVehicle.vehicle_reg };
            } else if (this.selectedVehicle.id !== null) {
              this.form.vehicle = this.selectedVehicle;
            } else {
              delete this.form.vehicle;
            }
            if (!this.form.parkingCardNr || !this.canUseCards()) {
              this.form.parkingCardNr = null;
            }
            if (this.isUnderUser && this.selectedProduct) {
              this.form.productId = this.selectedProduct;
            }
            if (this.isUnderUser && this.selectedTenant) {
              this.form.tenantId = this.selectedTenant;
            }
            if (this.form.validFromDate && this.form.validFromDate !== '') {
              this.form.validFromDate = moment(this.form.validFromDate, 'DD.MM.YYYY').toDate();
            } else {
              this.form.validFromDate = moment().toDate();
            }
            if (this.form.expirationDate && this.form.expirationDate !== '') {
              this.form.expirationDate = moment(this.form.expirationDate, 'DD.MM.YYYY').toDate();
            } else {
              delete this.form.expirationDate;
            }
            this.form.userId = this.selectedUser;
            this.form.id = this.permit.id;

            // add/update vehicle data
            if (this.form.vehicle !== undefined) {
              if (this.form.vehicle.id === undefined) {
                this.$store.dispatch('permits/permit/saveNewVehicle', {
                  vehicle_reg: this.form.vehicle.vehicleReg.toUpperCase(),
                  vehicleComment: this.form.vehicleComment,
                  userId: this.selectedUser,
                }).then((response) => {
                  const vehicleId = response.body.data.id;
                  this.form.vehicle.id = vehicleId;
                  this.form.vehicleId = vehicleId;
                }).then(() => {
                  this.saveVehiclePermit();
                }).catch((error) => {
                  const { message } = error.body;
                  this.showWarningModal = true;
                  this.userWarningHeader = this.$t('common.warning');
                  this.userWarningBody = this.$t(`errors.${message}`);
                });
              } else {
                this.form.vehicle.vehicleReg = this.form.vehicle.vehicle_reg;
                this.form.vehicleId = this.form.vehicle.id;
                this.saveVehiclePermit();
              }
            } else {
              this.saveVehiclePermit();
            }
          }
        });
    },
    saveVehiclePermit() {
      // Fix strange date problem
      const clonedForm = JSON.parse(JSON.stringify(this.form));
      clonedForm.validFromDate = moment(this.form.validFromDate).format();
      if (clonedForm.expirationDate) {
        clonedForm.expirationDate = moment(this.form.expirationDate).format();
      }

      this.$store.dispatch('permits/permit/savePermit', { user: this.user, permit: clonedForm })
        .then(() => {
          if (!this.permitError && !this.permit.vehicleRegError) {
            this.isPermitSelected = true;
            const updatedPermit = {};
            updatedPermit.id = this.permit.id; // parseInt(this.$route.params.id, 10);
            updatedPermit.parkingCardNr = this.form.parkingCardNr;
            updatedPermit.vehicleReg = this.selectedVehicle.vehicle_reg;
            if (this.permit.product
              && this.permit.product.zones
              && this.permit.product.zones.length > 0) {
              updatedPermit.address = this.permit.product.zones.map(v => `${v.code} (${v.address})`).join(', ');
            } else {
              updatedPermit.address = null;
            }
            if (this.permit.partner) {
              updatedPermit.partnerName = this.permit.partner.name;
            } else {
              updatedPermit.partnerName = null;
            }
            if (this.permit.product) {
              updatedPermit.productName = this.permit.product.name;
            } else {
              updatedPermit.productName = null;
            }
            if (this.permit.user) {
              updatedPermit.userName = this.permit.user.name;
            } else {
              updatedPermit.userName = null;
            }
            this.$store.commit(`landlord/permits/${mTypes.EDIT_DATA}`, updatedPermit);
            this.fetchAllData();
            this.fetchProducts();
            this.$router.push({
              name: 'user',
              params: { id: this.$route.params.id },
            });
          } else {
            this.showWarningModal = true;
            this.userWarningHeader = this.$t('common.warning');
            if (this.permit.vehicleRegError) {
              this.userWarningHeader = this.permit.vehicleRegError;
            }
            if (this.permitError.body.parkingCardNr) {
              this.userWarningBody = this.$t('errors.valid_card_field');
            } else if (!this.tenantHasProducts && this.tenantPermitCount > 0) {
              this.userWarningBody = this.$t('errors.all_permits_issued', { permitCount: this.tenantPermitCount });
            } else if (this.permitError.body.productId) {
              // this.userWarningBody = this.permitError.body.productId[0];
              this.userWarningBody = this.$t('errors.product_field_required');
            } else if (this.permitError.body.errors.productId) {
              // this.userWarningBody = this.permitError.body.errors.productId[0];
              this.userWarningBody = this.$t('errors.product_field_required');
            }
          }
        });
    },
    dynamicSort(property, a, b) {
      if (a[property] < b[property]) {
        return -1;
      } else if (a[property] > b[property]) {
        return 1;
      }
      return 0;
    },
    closeWarningModal() {
      this.showWarningModal = false;
    },
    resetValidators() {
      this.$validator.remove('validParkingCardNr');
      this.$validator.clean();
    },
    createValidators() {
      this.createParkingCardValidator();
    },
    createParkingCardValidator() {
      this.$validator.extend('validParkingCardNr', {
        getMessage: field => `The ${field} value is not valid parking card number.`,
        validate: (value) => {
          if (value === '') {
            return true;
          }
          // check if card number is 9 characters long and contains only hex characters
          return /^[0-9A-F]{9}$/i.test(value);
        },
      });
    },
    removeVehicle(vehicle) {
      if (vehicle.new) {
        if (vehicle.id === this.selectedVehicle.id) {
          this.showWarningModal = true;
          this.userWarningHeader = this.$t('common.warning');
          this.userWarningBody = this.$t('errors.vehicle_in_use');
        } else {
          const index = this.vehicles.data.findIndex(obj => obj.id === vehicle.id);
          this.$delete(this.vehicles.data, index);
          this.vehicles.total -= 1;
        }
        return;
      }

      if (vehicle.permitId.length === 0) {
        this.$validator.validateAll()
          .then((success) => {
            if (success) {
              this.$store.dispatch('users/vehicles/delete', { id: vehicle.id, user: this.user })
                .then(() => {
                  if (!this.vehicleError) {
                    const index = this.vehicles.data.findIndex(obj => obj.id === vehicle.id);
                    this.$delete(this.vehicles.data, index);
                    this.vehicles.total -= 1;
                  } else {
                    this.showWarningModal = true;
                    this.userWarningHeader = this.$t('common.warning');
                    this.userWarningBody = this.$t(`errors.${this.vehicleError.body.error}`);
                  }
                });
              this.$store.commit(`users/vehicles/${mTypes.DATA_ERROR}`, null);
            }
          });
      }
    },
    showDeletingConfirmation() {
      this.showConfirmationModal = true;
    },
    cancelDelete() {
      this.showConfirmationModal = false;
    },
    deletePermit() {
      this.showConfirmationModal = false;
      this.$store.dispatch('permits/permit/delete', { id: this.permit.id, user: this.user })
        .then(() => {
          if (this.userError) {
            this.userWarningHeader = this.$t('common.deleting');
            this.userWarningBody = this.$t('users.delete_error');
            this.showWarningModal = true;
          } else {
            this.$store.commit(`landlord/permits/${mTypes.REMOVE_DATA}`, this.permit.id);
            this.$router.push({ name: 'user', params: { id: this.$route.params.id } });
            this.fetchProducts();
          }
        })
        .catch(() => {
          this.$store.commit(`landlord/permits/${mTypes.DATA_ERROR}`, true);
        });
    },
    canUseCards() {
      if (this.selectedProduct != null && this.products.data) {
        for (let i = 0, l = this.products.data.length; i < l; i += 1) {
          if (this.products.data[i].id === this.selectedProduct) {
            return this.products.data[i].can_use_cards;
          }
        }
      }
      return false;
    },
    canUseDateExpiration() {
      if (this.selectedProduct != null && this.products.data) {
        for (let i = 0, l = this.products.data.length; i < l; i += 1) {
          if (this.products.data[i].id === this.selectedProduct) {
            return !this.products.data[i].date_expiration;
          }
        }
      }
      return false;
    },
    canSetPermitStartDate() {
      if (this.isLandlord()) {
        return true;
      }
      if (this.selectedProduct != null && this.products.data) {
        for (let i = 0, l = this.products.data.length; i < l; i += 1) {
          if (this.products.data[i].id === this.selectedProduct) {
            return this.products.data[i].tenant_manage_permit_start;
          }
        }
      }
      return false;
    },
    canShowPermitSpot() {
      if (this.isLandlord()) {
        return true;
      }
      if (this.selectedProduct != null && this.products.data) {
        for (let i = 0, l = this.products.data.length; i < l; i += 1) {
          if (this.products.data[i].id === this.selectedProduct) {
            if (!this.products.data[i].parking_spot_number &&
              !this.products.data[i].tenant_manage_permit_spot) {
              return false;
            }
          }
        }
      }
      return true;
    },
    canSetPermitSpot() {
      if (this.isLandlord()) {
        return true;
      }
      if (this.selectedProduct != null && this.products.data) {
        for (let i = 0, l = this.products.data.length; i < l; i += 1) {
          if (this.products.data[i].id === this.selectedProduct) {
            if (this.products.data[i].parking_spot_number) {
              return false;
            }
            return this.products.data[i].tenant_manage_permit_spot;
          }
        }
      }
      return false;
    },
    canSetPermitExpirationDate() {
      if (this.isLandlord()) {
        return true;
      }
      if (this.selectedProduct != null && this.products.data) {
        for (let i = 0, l = this.products.data.length; i < l; i += 1) {
          if (this.products.data[i].id === this.selectedProduct) {
            return this.products.data[i].tenant_manage_permit_expiration;
          }
        }
      }
      return false;
    },
    getFormattedDateExpiration(date) {
      return moment(date).format('DD.MM.YYYY');
    },
    permitValidityPeriod(permit) {
      if (permit.validFromDate && permit.expirationDate) {
        return this.$t('end_user.permit_validity', { from: permit.validFromDate, to: permit.expirationDate });
      }
      return '';
    },
    isPermitExpired(permit) {
      if (!permit.expirationDate) {
        return false;
      }

      return moment().isAfter(moment(permit.expirationDate, 'DD.MM.YYYY'), 'day');
    },
    showListModalVehicle() {
      this.showListModal = true;
      this.listModalType = 'vehicle';
    },
    showListModalProduct() {
      this.showListModal = true;
      this.listModalType = 'product';
    },
    closeListModal() {
      if (this.listModalType === 'vehicle') {
        if (this.primordialSelectedVehicle) {
          this.selectedVehicle = this.primordialSelectedVehicle;
        } else {
          this.selectedVehicle = {
            id: null,
          };
        }
        this.vehicleInputDisabled = true;
      }
      if (this.listModalType === 'product') {
        if (this.primordialSelectedProduct) {
          this.selectedProduct = this.primordialSelectedProduct;
        } else {
          this.selectedProduct = null;
        }
      }
      this.showListModal = false;
      this.listModalType = null;
    },
    confirmedListModal() {
      if (this.listModalType === 'vehicle') {
        if (this.form.vehicleReg) {
          this.addVehicleReg();
        }
        this.primordialSelectedVehicle = this.selectedVehicle;
      }
      if (this.listModalType === 'product') {
        this.primordialSelectedProduct = this.selectedProduct;
      }
      this.showListModal = false;
      this.listModalType = null;
    },
    setSelectedProductName(productId) {
      if (this.products.data) {
        this.products.data.forEach((item) => {
          if (item.id === productId) {
            this.selectedProductName = item.name;
            this.permitZoneAdd();
          }
        });
      }
    },
    fetchMorePlates() {
      this.vehicleOffset += this.vehicleLimit;
      this.$store.dispatch('users/vehicles/fetch', {
        id: this.selectedUser,
        user: this.user,
        searchTerm: this.plateSearch,
        offset: this.vehicleOffset,
        limit: this.vehicleLimit,
      });
    },
    searchPlateNumber() {
      this.vehicleOffset = 0;
      if (this.plateSearch !== '') {
        this.$store.dispatch('users/vehicles/fetch', {
          id: this.selectedUser,
          user: this.user,
          searchTerm: this.plateSearch,
          offset: this.vehicleOffset,
          limit: this.vehicleLimit,
        });
      } else {
        this.fetchVehiclePlateNumbers();
      }
    },
    clearPlateSearch() {
      if(this.plateSearch !== '') {
        this.plateSearch = '';
        this.vehicleOffset = 0;
        this.fetchVehiclePlateNumbers();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/app.scss";
@import "~styles/blocks/permit-edit.scss";
@import "~node_modules/leaflet/dist/leaflet.css";

.width-100 {
  width: 100% !important;
}

.user-details-container {
  background-color: var(--white);
  display: inline-block;
  margin: 0 0 0 20px;
  padding: 10px 35px;
  flex: 1;

  @media screen and (max-width: 1074px) {
    padding: 10px;
  }

  @media screen and (max-width: 760px) {
    padding: 0;
    margin: 0;
  }

  >.content {
    @media screen and (max-width: 1074px) {
      flex-direction: column;
    }
  }

  .product-map {
    @media screen and (max-width: 1074px) {
      flex-direction: column;
      margin-left: 0;
    }

    /*@media screen and (max-width: 760px) {
      display: none;
    }*/
  }

  .details-table__row:nth-child(2) {
    .details-table__col {
      padding-top: 7px;
      vertical-align: top;
    }
  }

  .details-table__row {
    .details-table__col {
      position: relative;
      padding-top: 7px;

      .el-input {
        border-spacing: 0;
        max-width: 285px;
        position: relative;
      }

      .el-button:not(.vehicle-input) {
        width: 54px;
      }

      .el-button {
        span {
          white-space: normal;
        }
      }
    }
  }
}

.details-table__row--vehicle {
  display: block;
  height: 100%;
  border: none;

  .details-table__col--vehicle {
    display: flex;
    flex-direction: column;
    padding: 0;
    height: 100%;

    .el-tag {
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 40px;
      text-transform: uppercase;
    }

    .vehicle-input {
      max-width: 100%;
      height: auto;
      margin: 2px;
    }

    .el-input-group {
      margin: 2px 0;
    }
  }
}

.user-details-container.mobile {
  display: inline-block;
  margin: 0 0 10px 0;
  width: 100%;

  @media screen and (min-width: 761px) {
    display: none;
  }
}

.permits-wrapper {
  .permit-details-wrapper {
    .permit-field {
      margin: 15px 0;
      padding: 12.5px 0;
      border-bottom: solid 1.5px var(--grey-four);
      display: flex;
      font-size: 16px;

      .permit-field-description {
        color: var(grey-nine);
        width: 20%;
        text-align: left;
        display: inline-block;
        margin: 0 25px;
        white-space: nowrap;
        font-size: 14px;
      }

      .permit-details {
        color: var(--black);
        font-weight: 900;
        width: 45%;
        text-align: left;
        display: inline-block;
        padding: 0 0 0 35px;
        flex: 1;
      }
    }
  }
}

.details-table {
  &__row {
    .el-select {
      width: 100%;
      position: relative;

      input {
        padding-right: 54px;
      }

      i {
        width: 54px;
        z-index: 2;
        color: #878d99;
      }
    }

    .el-tag {
      background-color: #f5f7fa;
      border: 1px solid #f5f7fa;
      margin: 2px;
      height: 40px;
      line-height: 40px;
      padding: 0 10px 0 15px;
      color: #000;
      cursor: pointer;

      /deep/ .el-tag__close {
        color: #000;
      }

      /deep/ .el-tag__close:hover {
        background-color: transparent;
      }
    }

    .el-tag {
      &.el-tag--success {
        background-color: var(--brand-green);
        border: 1px solid var(--brand-green);
        color: white;

        /deep/ .el-tag__close {
          color: white;
        }

        &.el-tag--permit /deep/ .el-tag__close {
          &::before {
            background: url('~icons/number-permit.svg') center/contain no-repeat;
          }
        }
      }

      &.el-tag--permit /deep/ .el-tag__close {
        width: 30px;
        height: 100%;

        &::before {
          content: '';
          background: url('~icons/number-permit-black.svg') center/contain no-repeat;
          height: 100%;
        }
      }
    }

    .el-tag:nth-last-child(2) {
      margin-right: -1px;
    }

    .el-input input {
      text-transform: uppercase;
    }
  }

  &.fixed-table {
    .details-table {
      &__row {
        display: flex;
        align-items: flex-end;
        flex-wrap: wrap;

        @media screen and (min-width: 1200px) {
          flex-wrap: nowrap;
        }
      }
    }
  }
}

.details-component {
  &__content-container {
    display: flex;

    @media screen and (max-width: 1600px) {
      flex-direction: column-reverse;
      align-items: flex-end;
    }
  }

  &__content {
    margin-right: 10px;

    @media screen and (max-width: 1600px) {
      margin: 0;
    }
  }
}

.permit-block {
  height: fit-content;
  min-width: 250px;
  box-shadow: none;
  margin: 0;

  @media screen and (max-width: 1600px) {
    width: 100%;
    margin-bottom: 9px;
  }

  &__zone {
    div {
      display: flex;
      align-content: center;

      &:not(:last-child) {
        margin-bottom: 5px;
      }
    }

    .permit-icon {
      display: inline-block;
      width: 16px;
      height: 16px;
      background-size: cover;
      margin-right: 10px;
    }
  }
}

.radiobuttons {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  & label {
    display: flex;
    align-items: center;
    justify-content: flex-start;

    & .radiobuttons__control {
      height: 31px;
    }

    & .radiobuttons__description {
      text-align: start;
    }
  }
}

.btn {
  &--change {
    display: inline-flex;
    text-transform: uppercase;
    background-color: var(--brand-green);
    color: var(--white);
  }
}
</style>

<style lang="scss">
@import "~styles/app.scss";
@import '~styles/blocks/permit.scss';

.el-button {
  vertical-align: middle;
}

.details-table {
  &__row {
    & .details-table__col {
      &--with-a-choise {
        display: flex;
        flex-wrap: wrap;
        height: auto !important;
        width: 100%;
      }

      .tooltip{
        left: 150px !important;
      }
    }

    .el-button {
      height: 40px;
      vertical-align: middle;

      span {
        margin: 0;
        white-space: normal;
      }
    }

    .el-input {
      &__suffix {
        z-index: 2;
        right: 0;
      }
    }

    .tooltip {
      right: auto;
      left: 50px;
      z-index: 2000;
      transform: translateY(-50%);
      max-height: 75px;

      &__content {
        @media screen and (max-width: 1000px) {
          max-height: none;
          height: auto;
          white-space: pre-wrap;
          max-width: 150px;
        }
      }
    }

    .tooltip.to-bottom {
      top: auto !important;
      bottom: 50px !important;
      transform: none;
    }
  }

  &__row:nth-child(2) {

    .details-table__col>.el-button {
      position: relative;
      padding: 10px 69px 10px 15px;
      border-color: #d8dce5;
      height: auto;
      max-width: 300px;
      margin: 2px;

      span {
        white-space: pre-line;
        line-height: 20px;

        i {
          position: absolute;
          right: 0;
          top: 0;
          height: 100%;
          width: 54px;
          border-left: 1px solid #d8dce5;
          margin-left: 15px;
          vertical-align: top;
          background-color: #f5f7fa;
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }

        i:before {
          position: absolute;
          right: 50%;
          top: 50%;
          transform: translate(50%, -50%);
          color: #97a8be;
        }
      }
    }
  }
}

.extend-default-button:after {
  content: "";
  position: absolute;
  top: 1px;
  right: 1px;
  width: 53px;
  height: 38px;
  border-left: 1px solid #d8dce5;
  background-color: #f5f7fa;
  color: #97a8be;
  border-bottom-right-radius: 4px;
  border-top-right-radius: 4px;
  z-index: 1;
}

.extend-default-button {
  .el-input {
    position: relative;

    input {
      padding-left: 56px;
    }

    &:before {
      font: normal normal normal 14px/1 FontAwesome;
      content: "\f041";
      padding: 0 20px;
      color: var(--grey-five);
      left: 0;
      top: 0;
      height: 100%;
      width: 56px;
      position: absolute;
      line-height: 40px;
    }
  }
}

.form {
  .el-input {
    &.parkingCardNrInput-toggle {
      padding: 0;
    }

    &__inner {
      border: 0;
      padding: 25px 0 0;
      background-color: var(--grey-ten) !important;
      line-height: normal;

      &--vehicle-reg {
        max-width: fit-content;
        padding: 12px 40px 10px;
        margin-right: 10px;
      }
    }

    .el-input-group {
      &__append {
        border-left: 1px solid #dcdfe6;
      }
    }
  }
}

.vehicle-input.el-button {
  position: relative;
  padding: 10px 69px 10px 15px;
  border-color: #d8dce5;
  height: auto;
  max-width: 250px;

  span {
    line-height: 20px;

    i {
      position: absolute;
      right: 0;
      top: 0;
      height: 100%;
      width: 54px;
      border-left: 1px solid #d8dce5;
      margin-left: 15px;
      vertical-align: top;
      background-color: #f5f7fa;
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }

    i:before {
      position: absolute;
      right: 50%;
      top: 50%;
      transform: translate(50%, -50%);
      color: #97a8be;
    }
  }
}

.vehicle-count {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  padding-bottom: 16px;

  &--count{
    font-size: 18px;
  }
}

.plateSearchWrapper{
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  width: 97%;
}

.plateSearchInput{
  input{
    text-transform: uppercase;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .el-input__inner {
    &::placeholder {
      color: var(--black);
      text-transform: none;
    }
  }
}

.plateSearchButton{
  border-left: none;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.el-input__suffix-inner{
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.clear-search{
  margin-right: 10px;
  margin-top: auto;
  margin-bottom: auto;
  cursor: pointer;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.change-plate-title{
  text-align: center;
  color: var(--brand-orange);
}




</style>



// WEBPACK FOOTER //
// src/components/users/AddEditPermitView.vue